import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SlidersHorizontal, X } from 'lucide-react';
import {
  CATALOG_LENGTHS,
  CATALOG_PAGE_SIZE,
  CATALOG_STATUSES,
  CATALOG_STATUS_LABELS,
  LEVELS,
  LEVEL_LABELS,
  TOPICS,
  type CatalogQuery,
} from '@ready/shared';
import { useApi } from '@/lib/api';
import { useDebounced } from '@/lib/use-debounced';
import { CatalogCard, SearchBox } from '@/components/catalog';
import { Button, Card, ErrorText, PageHeader, Select } from '@/components/ui';

const TYPES = [
  { id: '', label: 'All' },
  { id: 'course', label: 'Courses' },
  { id: 'self_assessment', label: 'Self-assessments' },
  { id: 'assessment', label: 'Assessments' },
  { id: 'workshop', label: 'Workshops' },
  { id: 'webinar', label: 'Webinars' },
] as const;

const isEventType = (type: string | null) => type === 'workshop' || type === 'webinar';

/** The filters that live in the URL (search and type are shown separately). */
const FILTERS = ['topic', 'source', 'level', 'length', 'status'] as const;

/**
 * Explore: everything the learner can take (Nirvik's library and their
 * organizations'), searchable and filterable, 20 at a time. Filters live in
 * the URL so results can be shared and survive Back.
 */
export function ExplorePage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const q = useDebounced(text.trim());
  const [showFilters, setShowFilters] = useState(false);

  const set = (changes: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(changes)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  // Typing updates the URL once it pauses.
  useEffect(() => {
    if ((params.get('q') ?? '') !== q) set({ q: q || null });
  }, [q]);

  const query: Partial<CatalogQuery> = {
    q: params.get('q') || undefined,
    type: (params.get('type') || undefined) as CatalogQuery['type'],
    topic: (params.get('topic') || undefined) as CatalogQuery['topic'],
    source: params.get('source') || undefined,
    level: (params.get('level') || undefined) as CatalogQuery['level'],
    length: (params.get('length') || undefined) as CatalogQuery['length'],
    status: (params.get('status') || undefined) as CatalogQuery['status'],
    // Workshops and webinars list the soonest first unless the learner picks another order.
    sort: (params.get('sort') ||
      (isEventType(params.get('type')) && !params.get('q')
        ? 'soonest'
        : 'relevance')) as CatalogQuery['sort'],
  };
  const results = useInfiniteQuery({
    queryKey: ['catalog', query],
    queryFn: ({ pageParam }) => api.catalog({ ...query, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });
  const first = results.data?.pages[0];
  const items = results.data?.pages.flatMap((p) => p.items) ?? [];
  // Sources only come with the first page; keep the last list while new results load.
  const [sources, setSources] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (first?.sources) setSources(first.sources);
  }, [first?.sources]);

  const active = FILTERS.filter((f) => params.get(f));
  const clearAll = () => set(Object.fromEntries(FILTERS.map((f) => [f, null])));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Explore"
        description="Courses, assessments, workshops and webinars from Nirvik Ready and your organizations."
      />

      <div className="space-y-3">
        <SearchBox value={text} onChange={setText} />
        <div
          className="flex items-center gap-2 overflow-x-auto pb-1"
          role="group"
          aria-label="Type"
        >
          {TYPES.map((t) => {
            const on = (params.get('type') ?? '') === t.id;
            return (
              <button
                key={t.id}
                type="button"
                aria-pressed={on}
                // On phones the row scrolls: keep the chosen type in view (e.g. from a link).
                ref={
                  on
                    ? (el) => el?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
                    : undefined
                }
                onClick={() => set({ type: t.id || null })}
                className={`min-h-10 shrink-0 rounded-full border px-4 text-sm ${
                  on
                    ? 'border-primary bg-primary/15 font-medium text-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[14rem_minmax(0,1fr)]">
        {/* Filters: a column on wide screens, behind a button on phones. */}
        <div>
          <Button
            variant="secondary"
            className="min-h-11 w-full lg:hidden"
            aria-expanded={showFilters}
            aria-controls="catalog-filters"
            onClick={() => setShowFilters((s) => !s)}
          >
            <SlidersHorizontal className="size-4" aria-hidden />
            Filters{active.length ? ` (${active.length})` : ''}
          </Button>
          <div
            id="catalog-filters"
            className={`${showFilters ? 'mt-3 block' : 'hidden'} space-y-4 lg:mt-0 lg:block`}
          >
            <FilterSelect
              id="f-topic"
              label="Topic"
              value={params.get('topic')}
              onChange={(v) => set({ topic: v })}
              options={TOPICS.map((t) => ({ value: t.id, label: t.label }))}
            />
            {sources.length > 1 && (
              <FilterSelect
                id="f-source"
                label="From"
                value={params.get('source')}
                onChange={(v) => set({ source: v })}
                options={sources.map((s) => ({ value: s.id, label: s.name }))}
              />
            )}
            <FilterSelect
              id="f-level"
              label="Level"
              value={params.get('level')}
              onChange={(v) => set({ level: v })}
              options={LEVELS.map((l) => ({ value: l, label: LEVEL_LABELS[l] }))}
            />
            <FilterSelect
              id="f-length"
              label="Length"
              value={params.get('length')}
              onChange={(v) => set({ length: v })}
              options={Object.entries(CATALOG_LENGTHS).map(([value, l]) => ({
                value,
                label: l.label,
              }))}
            />
            <FilterSelect
              id="f-status"
              label="Your progress"
              value={params.get('status')}
              onChange={(v) => set({ status: v })}
              options={CATALOG_STATUSES.map((s) => ({ value: s, label: CATALOG_STATUS_LABELS[s] }))}
            />
            {active.length > 0 && (
              <button
                type="button"
                className="-my-2 flex items-center gap-1 py-2 text-sm text-primary"
                onClick={clearAll}
              >
                <X className="size-4" aria-hidden /> Clear filters
              </button>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {results.isPending
                ? 'Searching…'
                : `${(first?.total ?? 0).toLocaleString()} result${first?.total === 1 ? '' : 's'}`}
            </p>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Sort
              <Select
                className="w-auto"
                value={query.sort === 'relevance' && !query.q ? 'newest' : query.sort}
                onChange={(e) =>
                  set({ sort: e.target.value === 'relevance' ? null : e.target.value })
                }
              >
                {query.q && <option value="relevance">Best match</option>}
                {(isEventType(params.get('type')) || query.sort === 'soonest') && (
                  <option value="soonest">Soonest</option>
                )}
                <option value="newest">Newest</option>
                <option value="popular">Most popular</option>
              </Select>
            </label>
          </div>
          <ErrorText error={results.error} />
          {results.isSuccess && items.length === 0 && (
            <Card>
              <p className="font-medium">Nothing matches yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Try fewer words or fewer filters.
              </p>
              {(active.length > 0 || query.q || query.type) && (
                <Button
                  variant="secondary"
                  className="mt-3"
                  onClick={() => {
                    setText('');
                    setParams({}, { replace: true });
                  }}
                >
                  Clear search and filters
                </Button>
              )}
            </Card>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((i) => (
              <CatalogCard key={`${i.kind}-${i.id}`} item={i} />
            ))}
          </div>
          {results.hasNextPage && (
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={results.isFetchingNextPage}
              onClick={() => results.fetchNextPage()}
            >
              {results.isFetchingNextPage
                ? 'Loading…'
                : `Show ${Math.min(CATALOG_PAGE_SIZE, (first?.total ?? 0) - items.length)} more`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
